import { HighlightOff, MoreVert, PlaylistAddCheck } from "@mui/icons-material";
import { Badge } from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { FloatingMenuButton, FloatingMenuTooltipWrapper } from "@ryde/controls";
import {
  bulkOperationsDrawerAF,
  coursesCalendarSelectedSlotsCountSelector,
  ICoursesCalendarSlotDto,
  normalizeCoursesCalendarSelectedSlotIds,
  selectedCoursesCalendarSlotIdsAtom,
  selectedCoursesCalendarSlotsSelector,
  useDrawers,
  useHasPermissions,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

interface ICoursesFloatingMenuCalendarInner {
  createSlotRide: (slot: ICoursesCalendarSlotDto) => Promise<number>;
  onAfterProduce: () => void;
  onRender: () => void;
}

export const CoursesFloatingMenuCalendarInner: FC<
  ICoursesFloatingMenuCalendarInner
> = ({ createSlotRide, onAfterProduce, onRender }) => {
  const { t } = useTranslation();
  const { openCoursesCalendarRideProduceDrawer } = useDrawers();
  const setBulkOperationsDrawerOpened = useSetRecoilState(
    bulkOperationsDrawerAF("course"),
  );
  const [selectedSlotIdsRaw, setSelectedSlotIds] = useRecoilState(
    selectedCoursesCalendarSlotIdsAtom,
  );
  const selectedSlotIds =
    normalizeCoursesCalendarSelectedSlotIds(selectedSlotIdsRaw);
  const selectedCount = useRecoilValue(
    coursesCalendarSelectedSlotsCountSelector,
  );
  const selectedSlots = useRecoilValue(selectedCoursesCalendarSlotsSelector);

  const { hasPermissions: canProduce } = useHasPermissions({
    entity: "booking",
    action: "produce",
  });

  const onClearSelection = useEvent((): void => {
    setSelectedSlotIds(new Set());
  });

  const handleProduceClick = useEvent(async (): Promise<void> => {
    await openCoursesCalendarRideProduceDrawer({
      slots: selectedSlots,
      createSlotRide,
      onAfterChange: onAfterProduce,
      shouldUseSharedProduceFlow: true,
    });
  });

  const handleOpenBulkOperationsDrawerClick = useEvent((): void => {
    setBulkOperationsDrawerOpened(true);
  });

  useEffect(() => {
    onRender();
  }, [onRender]);

  const badgeContent = useMemo(() => selectedCount, [selectedCount]);

  if (!selectedSlotIds.size) {
    return null;
  }

  return (
    <>
      <Badge color="primary" badgeContent={badgeContent}>
        <PlaylistAddCheck style={{ color: greyMui._700 }} />
      </Badge>

      <FloatingMenuTooltipWrapper tooltip={t("booking.bulk.cellClear")}>
        <FloatingMenuButton onClick={onClearSelection}>
          <HighlightOff sx={{ color: greyMui._700 }} />
        </FloatingMenuButton>
      </FloatingMenuTooltipWrapper>

      {canProduce && (
        <FloatingMenuButton onClick={() => void handleProduceClick()}>
          <SVG_MUI.ProductionSVG color={greyMui._700} />
          <span>{t("booking.bulk.produce")}</span>
        </FloatingMenuButton>
      )}

      <FloatingMenuButton onClick={handleOpenBulkOperationsDrawerClick}>
        <MoreVert sx={{ color: greyMui._700 }} />
      </FloatingMenuButton>
    </>
  );
};
